/**
 * RS / Contact — DVD-style bounce of the vertical wordmark.
 *
 * The wordmark drifts and bounces off the section edges. The rAF loop runs
 * only while the section is in the viewport; box and element sizes are
 * cached on resize (the prototype re-read getBoundingClientRect every
 * frame). Reduced motion: the wordmark sits still.
 */
const SPEED_X = 0.35;
const SPEED_Y = 0.28;

class RsContact extends HTMLElement {
  #raf = null;
  #observer = null;

  connectedCallback() {
    this.mark = this.querySelector('[data-rs-bounce]');
    if (!this.mark) return;

    this.x = 40;
    this.y = 60;
    this.vx = SPEED_X;
    this.vy = SPEED_Y;

    this.#measure();
    this.onResize = () => this.#measure();
    window.addEventListener('resize', this.onResize);

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          this.#start();
        } else {
          this.#stop();
        }
      }
    });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#stop();
    this.#observer?.disconnect();
    window.removeEventListener('resize', this.onResize);
  }

  #measure() {
    this.boxW = this.clientWidth;
    this.boxH = this.clientHeight;
    this.markW = this.mark.offsetWidth;
    this.markH = this.mark.offsetHeight;
  }

  #start() {
    if (this.#raf) return;
    this.#raf = requestAnimationFrame(this.#tick);
  }

  #stop() {
    cancelAnimationFrame(this.#raf);
    this.#raf = null;
  }

  #tick = () => {
    this.x += this.vx;
    this.y += this.vy;
    const maxX = this.boxW - this.markW;
    const maxY = this.boxH - this.markH;
    if (this.x <= 0) {
      this.x = 0;
      this.vx = Math.abs(this.vx);
    } else if (this.x >= maxX) {
      this.x = maxX;
      this.vx = -Math.abs(this.vx);
    }
    if (this.y <= 0) {
      this.y = 0;
      this.vy = Math.abs(this.vy);
    } else if (this.y >= maxY) {
      this.y = maxY;
      this.vy = -Math.abs(this.vy);
    }
    this.mark.style.transform = `translate(${this.x}px, ${this.y}px)`;
    this.#raf = requestAnimationFrame(this.#tick);
  };
}

if (!customElements.get('rs-contact')) {
  customElements.define('rs-contact', RsContact);
}
